import { Locale, Namespace } from './config'

// Translation cache
const translationCache: Record<string, any> = {}

export function getCachedTranslation(locale: Locale, namespace: Namespace): Record<string, any> | null {
  const cacheKey = `${locale}-${namespace}`
  return translationCache[cacheKey] || null
}

export async function loadTranslation(
  locale: Locale,
  namespace: Namespace
): Promise<Record<string, any>> {
  const cacheKey = `${locale}-${namespace}`
  
  // Return cached translation if available
  if (translationCache[cacheKey]) {
    return translationCache[cacheKey]
  }
  
  try {
    const translation = await import(`@/locales/${locale}/${namespace}.json`)
    translationCache[cacheKey] = translation.default || translation
    return translationCache[cacheKey]
  } catch (error) {
    console.error(`Failed to load translation for ${locale}/${namespace}:`, error)
    
    // Fallback to English
    if (locale !== 'en') {
      return loadTranslation('en', namespace)
    }
    
    return {}
  }
}

export async function loadTranslations(
  locale: Locale,
  namespaces: Namespace[]
): Promise<Record<Namespace, any>> {
  const translations: Record<string, any> = {}
  
  await Promise.all(
    namespaces.map(async (namespace) => {
      translations[namespace] = await loadTranslation(locale, namespace)
    })
  )
  
  return translations
}

// Helper function to get nested translation values
export function getTranslation(
  translations: Record<string, any>,
  key: string,
  params?: Record<string, string | number | boolean>
): any {
  const keys = key.split('.')
  let value: any = translations
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      console.warn(`Translation key not found: ${key}`)
      return key
    }
  }
  
  // If returnObjects is true, return as is (for arrays/objects)
  if (params?.returnObjects) {
    return value
  }
  
  if (typeof value !== 'string') {
    // Check if it's an array and returnObjects wasn't specified
    if (Array.isArray(value)) {
      return value
    }
    // Convert numbers to strings automatically
    if (typeof value === 'number') {
      return String(value)
    }
    console.warn(`Translation value is not a string: ${key}`)
    return key
  }
  
  // Replace parameters
  if (params) {
    Object.entries(params).forEach(([param, val]) => {
      if (param !== 'returnObjects') {
        value = value.replace(new RegExp(`{{${param}}}`, 'g'), String(val))
      }
    })
  }
  
  return value
}